import { Drawer } from 'theme';

class InquiryDrawerV2 extends Drawer {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();

        // Prevent duplicate setup
        if (this.hasAttribute('data-initialized')) {
            return;
        }
        this.setAttribute('data-initialized', 'true');

        this.checkUrlAndOpen();
        this.setupFormValidation();
        this.restoreFormData();
    }

    checkUrlAndOpen() {
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        if (urlParams.get('form_type') === 'contact' || hash.includes('inquiry-form')) {
            this.show();
        }
    }

    setupFormValidation() {
        const form = this.querySelector('#inquiry-contact-form');
        if (!form) return;

        const inputs = form.querySelectorAll('input[required], textarea[required], select[required]');

        inputs.forEach((input) => {
            input.addEventListener('blur', () => {
                this.validateField(input);
            });

            input.addEventListener('input', () => {
                if (input.value.trim()) {
                    input.classList.remove('!border-red-500', '!bg-red-50');
                    input.style.borderColor = '';
                    input.removeAttribute('aria-invalid');
                }
                this.saveFormData();
            });
        });
    }

    validateField(field) {
        const value = field.value.trim();
        let isValid = true;

        field.classList.remove('!border-red-500', '!bg-red-50');
        field.style.borderColor = '';
        field.removeAttribute('aria-invalid');

        if (!value) {
            isValid = false;
        } else if (field.type === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
            }
        } else if (field.type === 'tel') {
            const phoneRegex = /^[\d\s\-\+\(\)]+$/;
            if (!phoneRegex.test(value)) {
                isValid = false;
            }
        }

        if (!isValid) {
            field.classList.add('!border-red-500', '!bg-red-50');
            field.style.borderColor = '#ef4444';
            field.setAttribute('aria-invalid', 'true');
        }

        return isValid;
    }

    getProductId() {
        const productInput = this.querySelector('input[name="contact[product]"]');
        return productInput ? productInput.value : window.location.pathname.split('/').pop();
    }

    getGlobalFields() {
        return ['contact[first_name]', 'contact[last_name]', 'contact[name]', 'contact[email]', 'contact[country]', 'contact[phone]'];
    }

    getProductFields() {
        // Get all contact[...] fields that are not in global fields
        const form = this.querySelector('#inquiry-contact-form');
        if (!form) return [];

        const allFields = Array.from(form.querySelectorAll('input, textarea, select'))
            .map(field => field.name)
            .filter(name => name && name.startsWith('contact[') && !this.getGlobalFields().includes(name));

        return [...new Set(allFields)]; // Remove duplicates
    }

    saveFormData() {
        const form = this.querySelector('#inquiry-contact-form');
        if (!form) return;

        const productId = this.getProductId();
        const globalFields = this.getGlobalFields();
        const productFields = this.getProductFields();

        // Save global fields (persist across products)
        const globalData = {};
        globalFields.forEach((fieldName) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field && field.value.trim()) {
                globalData[fieldName] = field.value;
            }
        });

        if (Object.keys(globalData).length > 0) {
            localStorage.setItem('inquiry_form_global_v2', JSON.stringify(globalData));
        }

        // Save product-specific fields (expire after 15 minutes)
        const productData = {
            timestamp: Date.now(),
            data: {}
        };

        productFields.forEach((fieldName) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field && field.value.trim()) {
                productData.data[fieldName] = field.value;
            }
        });

        if (Object.keys(productData.data).length > 0) {
            localStorage.setItem(`inquiry_form_product_v2_${productId}`, JSON.stringify(productData));
        }
    }

    restoreFormData() {
        const form = this.querySelector('#inquiry-contact-form');
        if (!form) return;

        // Restore global fields
        try {
            const globalData = localStorage.getItem('inquiry_form_global_v2');
            if (globalData) {
                const data = JSON.parse(globalData);
                Object.entries(data).forEach(([fieldName, value]) => {
                    const field = form.querySelector(`[name="${fieldName}"]`);
                    if (field && !field.value) {
                        field.value = value;
                    }
                });
            }
        } catch (e) {
            console.warn('Failed to restore global form data:', e);
        }

        // Restore product-specific fields
        try {
            const productId = this.getProductId();
            const productData = localStorage.getItem(`inquiry_form_product_v2_${productId}`);

            if (productData) {
                const data = JSON.parse(productData);
                const now = Date.now();
                const fifteenMinutes = 15 * 60 * 1000;

                if (now - data.timestamp < fifteenMinutes) {
                    Object.entries(data.data).forEach(([fieldName, value]) => {
                        const field = form.querySelector(`[name="${fieldName}"]`);
                        if (field && !field.value) {
                            field.value = value;
                        }
                    });
                } else {
                    localStorage.removeItem(`inquiry_form_product_v2_${productId}`);
                }
            }
        } catch (e) {
            console.warn('Failed to restore product form data:', e);
        }
    }

    clearFormData() {
        const productId = this.getProductId();
        localStorage.removeItem(`inquiry_form_product_v2_${productId}`);
    }
}

if (!window.customElements.get('inquiry-drawer-v2')) {
    window.customElements.define('inquiry-drawer-v2', InquiryDrawerV2);
}
